<template>
  <div class="coupon_page">
    <div class="coupon_page_header">
      <navbar title="優惠券"></navbar>
    </div>
    <div class="coupon_hero">
      <h3 class="coupon_title">{{ getTitle }}</h3>
      <span class="coupon_badge">{{ getBadgeText }}</span>
    </div>

    <p class="coupon_message" v-if="getLoadError">{{ getLoadError }}</p>
    <couponLoading v-else-if="isLoading && !hasDetail" />

    <!-- 已領取：名稱卡片下方直接出示當次查詢確認的 QR；已使用／已失效由標題徽章表示，不顯示此卡 -->
    <div class="coupon_qr_card" v-if="isUseMode && memberCoupon && !getLoadError && !isTerminalStatus">
      <couponLoading v-if="isLoading" />
      <template v-else-if="canShowQr">
        <h3>請將 QR 碼給店員掃描</h3>
        <div class="qr_code_frame">
          <QRCode :value="qrPayload" :size="250" level="H" />
        </div>
      </template>
      <p class="qr_hint" v-else>{{ getUnusableText(memberCoupon) }}</p>
    </div>

    <!-- 未領取：母券詳情，確認後才領取 -->
    <div class="coupon_detail_card" v-if="isClaimMode && templateDetail">
      <couponImage :image="getCouponImage" variant="banner" :alt="`${getTitle} 優惠券圖片`" />
      <template v-if="templateDetail.description">
        <h4>優惠說明</h4>
        <p>{{ templateDetail.description }}</p>
      </template>
      <h4>使用門檻</h4>
      <p v-for="text in getPublicRuleDetail.thresholdTexts" :key="text">{{ text }}</p>
      <h4>優惠內容</h4>
      <p v-for="text in getPublicRuleDetail.benefitTexts" :key="text">{{ text }}</p>
      <h4>領取期間</h4>
      <p>{{ getClaimPeriodText }}</p>
      <template v-if="getTemplateValidityText">
        <h4>使用期限</h4>
        <p>{{ getTemplateValidityText }}</p>
      </template>
      <h4>使用方式</h4>
      <p>領取後至「已領取」出示優惠券，交由門市人員掃碼確認。</p>
    </div>

    <!-- 已領取：子券詳情，顯示實際效期與目前公開規則 -->
    <div class="coupon_detail_card" v-if="isUseMode && memberCoupon">
      <couponImage :image="getCouponImage" variant="banner" :alt="`${getTitle} 優惠券圖片`" />
      <p class="coupon_notice" v-if="getValidFromNotice">
        {{ getValidFromNotice }}
      </p>
      <p class="coupon_notice" v-if="isSelectedWithOrder">
        占用訂單識別碼：{{ memberCoupon.selected_order_uuid }}
      </p>
      <template v-if="getMemberCouponDescription">
        <h4>優惠說明</h4>
        <p>{{ getMemberCouponDescription }}</p>
      </template>
      <h4>使用門檻</h4>
      <p v-for="text in getPublicRuleDetail.thresholdTexts" :key="text">{{ text }}</p>
      <h4>優惠內容</h4>
      <p v-for="text in getPublicRuleDetail.benefitTexts" :key="text">{{ text }}</p>
      <h4>領取時間</h4>
      <p>{{ getClaimedAtText }}</p>
      <h4>使用期限</h4>
      <p>{{ getValidPeriodText }}</p>
    </div>

    <div class="coupon_confirm" v-if="isClaimMode">
      <!-- 本期已領過：提供入口讓會員自行查看，不自動導頁 -->
      <button
        type="button"
        class="usage_button claimed_link_button"
        v-if="isClaimMode && isAlreadyClaimed"
        @click="toClaimedTab"
      >
        查看已領取
      </button>
      <button
        type="button"
        class="usage_button"
        v-if="isClaimMode"
        :disabled="!canClaim"
        @click="claim"
      >
        確認領取
      </button>
    </div>
  </div>
</template>
<script>
import QRCode from "qrcode.vue";
import navbar from "@/components/general/navbar.vue";
import couponLoading from "@/components/coupon/couponLoading.vue";
import couponImage from "@/components/coupon/couponImage.vue";
import commonUtilities from "@/utils/common.utils";
import {
  ALREADY_CLAIMED_IN_PERIOD,
  COUPON_BIRTHDAY_REQUIRED,
  BIRTHDAY_UNREADABLE_TEXT,
  canUseCoupon,
  COUPON_DATE_FORMAT,
  formatCouponDateTime,
  getCouponStatusText,
  getCouponValidFromText,
  getCouponValidFromAt,
  getCouponValidUntilAt,
  isCouponExpired,
} from "@/utils/coupon.utils";
import { getCouponPublicRuleDetail, getTemplateValidityText } from "@/utils/couponTemplate.utils";
import { getBirthdayFormRoute } from "@/utils/couponClaimLink.utils";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// setTimeout 上限約 24.8 天，長效期分段計時。
const MAX_TIMER_DELAY = 2147483647;

export default {
  components: {
    QRCode,
    navbar,
    couponLoading,
    couponImage,
  },
  data() {
    return {
      qrPayload: "",
      qrCouponId: "",
      qrIdentity: "",
      qrStoreCode: "",
      detailRequestNo: 0,
      isDetailActive: true,
      isPageVisible: document.visibilityState === "visible",
      claimErrorCode: "",
      nowMs: Date.now(),
      validFromTimer: null,
    };
  },
  computed: {
    usageType() {
      const params = this.$route.params;
      return params.method;
    },
    couponId() {
      const params = this.$route.params;
      return params.id;
    },
    storeCode() {
      return this.$store.getters["userData/storeCode"];
    },
    getMemberIdentity() {
      return this.$store.getters["userData/storeMemberUuid"];
    },
    // 舊版數字 coupons_id 不送進 v2 API
    isValidCouponId() {
      return (
        typeof this.couponId === "string" && UUID_PATTERN.test(this.couponId)
      );
    },
    isClaimMode() {
      return this.usageType === "claim" && this.isValidCouponId;
    },
    isUseMode() {
      return this.usageType === "use" && this.isValidCouponId;
    },
    templateDetailState() {
      const state = this.$store.getters["coupon/couponTemplateDetail"];
      return state.uuid === this.couponId ? state : null;
    },
    memberCouponState() {
      const state = this.$store.getters["coupon/memberCouponDetailV2"];
      return state.uuid === this.couponId ? state : null;
    },
    detailState() {
      if (this.isClaimMode) return this.templateDetailState;
      if (this.isUseMode) return this.memberCouponState;
      return null;
    },
    templateDetail() {
      return this.templateDetailState?.data || null;
    },
    memberCoupon() {
      return this.memberCouponState?.data || null;
    },
    hasDetail() {
      return Boolean(this.detailState?.data);
    },
    isLoading() {
      return Boolean(this.detailState?.loading);
    },
    getLoadError() {
      if (!this.isValidCouponId || !["claim", "use"].includes(this.usageType)) {
        return "此優惠券連結已失效，請從優惠券列表重新進入";
      }
      return this.detailState?.error || "";
    },
    isClaiming() {
      return this.$store.state.coupon.isClaiming;
    },
    isAlreadyClaimed() {
      return this.claimErrorCode === ALREADY_CLAIMED_IN_PERIOD;
    },
    canClaim() {
      return (
        this.isClaimMode &&
        Boolean(this.templateDetail?.offer_uuid) &&
        !this.isLoading &&
        !this.isClaiming
      );
    },
    canShowQr() {
      return (
        this.isUseMode &&
        this.isDetailActive &&
        this.isPageVisible &&
        Boolean(this.memberCoupon) &&
        !this.isLoading &&
        !this.getLoadError &&
        Boolean(this.qrPayload) &&
        this.qrCouponId === this.couponId &&
        this.qrIdentity === this.getMemberIdentity &&
        this.qrStoreCode === this.storeCode &&
        this.qrStoreCode === this.$route.params.store &&
        this.qrPayload === this.memberCoupon.qr_payload &&
        !this.isCouponExpired(this.memberCoupon) &&
        canUseCoupon(this.memberCoupon, this.nowMs)
      );
    },
    getBadgeText() {
      if (this.isClaimMode) return "可領取";
      if (this.memberCoupon) return getCouponStatusText(this.memberCoupon, this.nowMs);
      return "優惠券";
    },
    isTerminalStatus() {
      return ["已使用", "已失效"].includes(getCouponStatusText(this.memberCoupon, this.nowMs));
    },
    // 尚未生效時提示實際開始時間；徽章依 API effective 狀態顯示。
    getValidFromNotice() {
      return getCouponValidFromText(this.memberCoupon, this.nowMs);
    },
    getTitle() {
      if (this.isClaimMode) return this.templateDetail?.title || "";
      const rules =
        this.memberCoupon?.current_public_rules ||
        this.memberCoupon?.claimed_public_rules;
      return rules?.title || this.memberCoupon?.title || "";
    },
    getCouponImage() {
      if (this.isClaimMode) return this.templateDetail?.image ?? null;
      if (this.isUseMode) return this.memberCoupon?.image ?? null;
      return null;
    },
    getClaimPeriodText() {
      const zone = this.templateDetail?.timezone_name;
      const begin = formatCouponDateTime(
        this.templateDetail?.claim_begin_at,
        zone,
        COUPON_DATE_FORMAT
      );
      const end = formatCouponDateTime(
        this.templateDetail?.claim_end_at,
        zone,
        COUPON_DATE_FORMAT
      );
      return `${begin} ～ ${end}`;
    },
    // 領取前只顯示母券規則（例如領取後 30 天），不虛構子券到期日
    getTemplateValidityText() {
      return getTemplateValidityText(this.templateDetail?.public_rules, this.templateDetail?.timezone_name);
    },
    // 母券目前已清空說明時不回退至領取時的歷史說明
    getMemberCouponDescription() {
      return this.memberCoupon?.current_public_rules?.description ?? "";
    },
    getPublicRuleDetail() {
      return getCouponPublicRuleDetail({
        publicRules: this.isClaimMode ? this.templateDetail?.public_rules : this.memberCoupon?.current_public_rules,
      });
    },
    isSelectedWithOrder() {
      return (
        this.memberCoupon?.status === "selected" &&
        typeof this.memberCoupon.selected_order_uuid === "string" &&
        this.memberCoupon.selected_order_uuid.trim().length > 0
      );
    },
    getClaimedAtText() {
      return formatCouponDateTime(
        this.memberCoupon?.claimed_at,
        this.memberCoupon?.timezone_name
      );
    },
    getValidPeriodText() {
      const zone = this.memberCoupon?.timezone_name;
      return `${formatCouponDateTime(
        getCouponValidFromAt(this.memberCoupon),
        zone
      )} ～ ${formatCouponDateTime(getCouponValidUntilAt(this.memberCoupon), zone)}`;
    },
  },
  watch: {
    // 同元件換券時清掉舊 QR 與訊息，再載入新資料
    "$route.params": {
      handler() {
        if (this.$route.name === "useCoupon") this.load();
      },
    },
    getMemberIdentity() {
      if (this.$route.name === "useCoupon") this.load();
    },
    storeCode() {
      if (this.$route.name === "useCoupon") this.load();
    },
  },
  mounted() {
    this.load();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeUnmount() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.isDetailActive = false;
    this.resetQr();
  },
  methods: {
    load() {
      this.resetQr();
      this.claimErrorCode = "";
      this.nowMs = Date.now();
      if (this.isClaimMode) {
        this.$store.dispatch("coupon/fetchCouponTemplateDetail", this.couponId);
      } else if (this.isUseMode && this.getMemberIdentity && this.isPageVisible) {
        this.refreshMemberCoupon();
      }
    },
    async refreshMemberCoupon() {
      this.resetQr();
      this.nowMs = Date.now();
      if (
        !this.isUseMode || !this.getMemberIdentity ||
        !this.isPageVisible || !this.isDetailActive
      ) return null;
      const couponId = this.couponId;
      const identity = this.getMemberIdentity;
      const storeCode = this.storeCode;
      const requestNo = this.detailRequestNo;
      const data = await this.$store.dispatch(
        "coupon/fetchMemberCouponDetailV2",
        couponId
      );
      if (
        !this.isDetailActive || !this.isPageVisible || !this.isUseMode ||
        requestNo !== this.detailRequestNo || couponId !== this.couponId ||
        identity !== this.getMemberIdentity || storeCode !== this.storeCode
      ) return null;
      this.nowMs = Date.now();
      if (!data) return null;
      if (canUseCoupon(data, this.nowMs) && !this.isCouponExpired(data)) {
        this.qrPayload = data.qr_payload;
        this.qrCouponId = couponId;
        this.qrIdentity = identity;
        this.qrStoreCode = storeCode;
      }
      this.scheduleValidFromUpdate(data);
      return data;
    },
    // 生效／到期邊界先移除 QR 再重查；長效期分段計時，沒有固定輪詢
    scheduleValidFromUpdate(data) {
      clearTimeout(this.validFromTimer);
      if (data?.status !== "available") return;
      const upcoming = [getCouponValidFromAt(data), getCouponValidUntilAt(data)]
        .map((value) => Date.parse(value))
        .filter((ms) => Number.isFinite(ms) && ms > this.nowMs);
      if (!upcoming.length) return;
      const nextBoundary = Math.min(...upcoming);
      const requestNo = this.detailRequestNo;
      this.validFromTimer = setTimeout(() => {
        if (requestNo !== this.detailRequestNo || !this.isDetailActive) return;
        this.nowMs = Date.now();
        if (this.nowMs >= nextBoundary) {
          this.refreshMemberCoupon();
        } else {
          this.scheduleValidFromUpdate(data);
        }
      }, Math.min(nextBoundary - this.nowMs, MAX_TIMER_DELAY));
    },
    isCouponExpired(data) {
      return isCouponExpired(data, this.nowMs);
    },
    resetQr() {
      this.detailRequestNo += 1;
      this.qrPayload = "";
      this.qrCouponId = "";
      this.qrIdentity = "";
      this.qrStoreCode = "";
      clearTimeout(this.validFromTimer);
    },
    async claim() {
      if (!this.canClaim) return;
      const couponId = this.couponId;
      this.claimErrorCode = "";
      try {
        const result = await this.$store.dispatch(
          "coupon/claimCouponTemplate",
          couponId
        );
        if (!result || couponId !== this.couponId) return;
        // 成功後切至已領取，列表頁重查子券與可領母券；刷新失敗只重試查詢，不再次領取
        this.$router.replace({
          path: `/${this.storeCode}/coupon`,
          query: { tab: "claimed" },
        });
      } catch (error) {
        // 生日未填時引導填寫，其餘錯誤以純文字顯示
        if (couponId === this.couponId) {
          this.claimErrorCode = error?.code || "";
          const isBirthdayRequired = error?.code === COUPON_BIRTHDAY_REQUIRED;
          if (
            isBirthdayRequired &&
            !this.$store.state.userData?.memberInfo?.birthday
          ) {
            const { isConfirmed } = await commonUtilities.createConfirm(
              "warning",
              "請先填寫生日",
              error.message,
              "前往填寫生日",
              "取消"
            );
            if (isConfirmed && couponId === this.couponId) {
              this.$router.push(
                getBirthdayFormRoute(this.storeCode, { templateUuid: couponId })
              );
            }
            return;
          }
          // 用 text 顯示（純文字），避免訊息被當成 HTML
          commonUtilities.createToast(
            "error",
            "領取失敗",
            isBirthdayRequired
              ? BIRTHDAY_UNREADABLE_TEXT
              : error?.message || "領取失敗，請稍後再試"
          );
        }
      }
    },
    // 狀態仍為可使用卻不可用（如缺 QR）時不顯示「目前無法使用：可使用」
    getUnusableText(data) {
      const validFromText = getCouponValidFromText(data, this.nowMs);
      if (validFromText) return validFromText;
      const statusText = getCouponStatusText(data, this.nowMs);
      return statusText === "可使用"
        ? "目前無法使用，請稍後再試"
        : `目前無法使用：${statusText}`;
    },
    toClaimedTab() {
      this.$router.push({
        path: `/${this.storeCode}/coupon`,
        query: { tab: "claimed" },
      });
    },
    onVisibilityChange() {
      this.isPageVisible = document.visibilityState === "visible";
      this.resetQr();
      if (this.isPageVisible && this.isUseMode) this.refreshMemberCoupon();
    },
  },
};
</script>
<style lang="scss" scoped>
// 捲動時 header 固定在上方，與優惠券列表頁一致
.coupon_page_header {
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  flex-shrink: 0;
  background-color: #F0F0F0;
}

.coupon_hero {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: calc(100% - 28px);
  margin: 14px 14px 0;
  padding: 19px 15px;
  background-color: #fff4e7;
  border: 1px solid #f1d9bb;
  border-radius: 10px;

  .coupon_badge {
    flex-shrink: 0;
    padding: 4px 10px;
    border-radius: 4px;
    background-color: #fff2e8;
    color: #9b4e1b;
    font-size: 17px;
    line-height: 1.4;
    white-space: nowrap;
  }

  .coupon_title {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.4;
    color: #292929;
    overflow-wrap: anywhere;
  }
}

.coupon_detail_card {
  box-sizing: border-box;
  width: calc(100% - 28px);
  margin: 14px;
  padding: 18px;
  background-color: #fff;
  border-radius: 8px;
  color: #292929;
  font-size: 18px;
  line-height: 1.6;

  h4 {
    margin: 15px 0 4px;
    font-size: 16px;
    font-weight: 500;
    color: #8b7563;
  }

  // 圖片在卡片最上方時，緊接的標題同樣不留上邊距
  h4:first-child,
  .coupon_image_banner + h4 {
    margin-top: 0;
  }

  p {
    margin: 4px 0;
    overflow-wrap: anywhere;
  }

  .coupon_notice {
    margin: 0 0 12px;
    font-size: 17px;
    color: #a45721;
  }
}

.coupon_qr_card {
  box-sizing: border-box;
  width: calc(100% - 28px);
  margin: 14px 14px 0;
  padding: 20px 16px;
  border-radius: 8px;
  background-color: #fff;
  text-align: center;
  color: #292929;

  h3 {
    margin: 0;
    color: #fd5001;
    font-size: 19px;
    font-weight: 700;
    line-height: 1.6;
  }

  .qr_code_frame {
    box-sizing: border-box;
    width: 282px;
    padding: 16px;
    margin: 8px auto;
    background-color: #fff;
    line-height: 0;
  }

  .qr_hint {
    margin: 4px 0;
    font-size: 14px;
  }
}

@media (max-width: 342px) {
  .coupon_qr_card {
    padding-right: 5px;
    padding-left: 5px;
  }
}

.coupon_message {
  width: calc(100% - 28px);
  margin: 10px 14px 0;
  text-align: center;
  font-size: 14px;
  color: #d93025;
}

.coupon_confirm {
  box-sizing: border-box;
  width: 100%;
  padding: 0 14px 19px;

  .claimed_link_button {
    margin-bottom: 10px;
    background-color: #fff;
    border: 1px solid #fd5001;
    color: #fd5001;
  }
}

.usage_button {
  display: block;
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 6px;
  background-color: #fbcb02;
  color: #242424;
  font-weight: 600;
  font-family: PingFang SC;
  font-size: 15px;
  line-height: 1.5;

  &:disabled {
    background-color: #ddd;
    color: #777;
  }
}

</style>
